import React, {
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import * as d3 from "d3";
import { Grid, Paper, Typography } from "@mui/material";
import { InteractiveGlidepathMetric } from "./data/InteractiveGlidepathMetric";
import { Constants } from "constants/constant";
import { downloadChartImage } from "utilities/downloadChartImage";
import { positionTooltip } from "utilities/chartTooltip";
import { renderTargetMarkerLines } from "utilities/chartTargetMarkers";
import { formatEmissionValue } from "utilities/utilities";

const UNIT_TCO2E = Constants.UNIT_TCO2E;

const SCOPE_PALETTE = [
  "#4472c4", "#70ad47", "#ffc000", "#7030a0",
  "#00b0f0", "#f7941d", "#92d050", "#00b050",
];

const getScopeColor = (scopeKeys: string[], scope: string): string =>
  SCOPE_PALETTE[scopeKeys.indexOf(scope) % SCOPE_PALETTE.length] ?? "#c5d4e8";

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

interface InteractiveGlidepathProps {
  emissionsData: InteractiveGlidepathMetric[];
}

export type InteractiveGlidepathRef = {
  downloadChart: (format: "png" | "jpeg") => void;
};

type SafeInteractiveGlidepathMetric = InteractiveGlidepathMetric & {
  year: string | number;
  ghgEmissions?: number;
  residualEmissions: number;
  target?: number;
  isHistoricalEmissionData: boolean;
  isBaseLine: boolean;
  reductionsByScope?: Record<string, number> | null;
};

const toSafeNumber = (value: unknown, fallback = 0): number => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const toOptionalSafeNumber = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }

  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
};

const toSafeYear = (value: unknown, fallback: string | number): string | number => {
  if (typeof value === "string" && value.trim() !== "") {
    return value;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  return fallback;
};

const sanitizeEmissionsData = (
  input?: InteractiveGlidepathMetric[]
): SafeInteractiveGlidepathMetric[] => {
  if (!Array.isArray(input)) {
    return [];
  }

  return input
    .filter((item) => item != null && typeof item === "object")
    .map((item: any, index) => ({
      ...item,
      year: toSafeYear(item?.year, `Unknown-${index + 1}`),
      ghgEmissions: toOptionalSafeNumber(item?.ghgEmissions),
      residualEmissions: toSafeNumber(item?.residualEmissions, 0),
      target: toOptionalSafeNumber(item?.target),
      isHistoricalEmissionData: Boolean(item?.isHistoricalEmissionData),
      isBaseLine: Boolean(item?.isBaseLine),
      // normalise API casing: backend may return reductionsByScope (camelCase)
      reductionsByScope: item?.reductionsByScope ?? item?.ReductionsByScope ?? null,
    }))
    .sort((a, b) => Number(a.year) - Number(b.year));
};

const InteractiveGlidepath = forwardRef<
  InteractiveGlidepathRef,
  InteractiveGlidepathProps
>(({ emissionsData }, ref) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const safeData = useMemo(
    () => sanitizeEmissionsData(emissionsData),
    [emissionsData]
  );

  const hasTargetData = safeData.some(
    (d) =>
      d.target !== undefined &&
      d.target !== null &&
      Number.isFinite(Number(d.target))
  );

  const scopeKeys = useMemo(
    () =>
      Array.from(
        new Set(
          safeData.flatMap((d) =>
            d.reductionsByScope ? Object.keys(d.reductionsByScope) : []
          )
        )
      ).sort(),
    [safeData]
  );

  const getBarColor = (d: SafeInteractiveGlidepathMetric) => {
    if (d.isHistoricalEmissionData && !d.isBaseLine) return "#5B9BD5";
    if (d.isBaseLine) return "#ED7D31";
    return "#c5d4e8";
  };

  const getEmissionDataLabel = (d: SafeInteractiveGlidepathMetric) => {
    if (!d.isHistoricalEmissionData) return "Projected";
    return d.isBaseLine ? "Baseline Year" : "Historical";
  };

  const handleDownload = (format: "png" | "jpeg") => {
    if (!svgRef.current) {
      console.warn("InteractiveGlidepath SVG reference is missing.");
      return;
    }

    if (!safeData.length) {
      console.warn("No valid target trajectory data available for download.");
      return;
    }

    const legendItems = [
      { label: "Historical Emissions (Actual)", color: "#5B9BD5" },
      { label: "Baseline Year", color: "#ED7D31" },
      { label: "Projected Residual Emissions", color: "#c5d4e8" },
      { label: "Historical Emissions Trajectory", color: "#1a1a2e" },
      { label: "BAU GHG Emissions (Projected)", color: "#1a1a2e" },
      ...(hasTargetData
        ? [{ label: "Targeted GHG Emissions", color: "#f59e0b" }]
        : []),
    ];

    downloadChartImage({
      svgRef,
      format,
      fileName: `target_trajectory.${format}`,
      title: "Target Trajectory",
      subtitle: "Residual emissions vs target over time",
      legendItems,
      legendConfig: {
        marginLeft: 40,
        marginTop: 60,
        itemWidth: 180,
        itemHeight: 20,
      },
      titlePosition: "bottom",
    });
  };

  useImperativeHandle(ref, () => ({
    downloadChart: handleDownload,
  }));

  useEffect(() => {
    if (!svgRef.current) {
      return;
    }


    const rootSvg = d3.select(svgRef.current);
    rootSvg.selectAll("*").remove();

    if (!safeData.length) {
      rootSvg
        .attr("width", 1100)
        .attr("height", 320)
        .append("text")
        .attr("x", 220)
        .attr("y", 140)
        .attr("fill", "#64748b")
        .style("font-size", "16px")
        .text("No valid data available for Target Trajectory.");
      return;
    }

    const margin = { top: 20, right: 50, bottom: 40, left: 75 };
    const width = 1100 - margin.left - margin.right;
    const height = 420 - margin.top - margin.bottom;

    const years = safeData.map((d) => String(d.year));
    const maxDomainCandidate = d3.max(safeData, (d) =>
      Math.max(
        d.ghgEmissions !== undefined ? toSafeNumber(d.ghgEmissions, 0) : 0,
        toSafeNumber(d.residualEmissions, 0),
        d.target !== undefined ? toSafeNumber(d.target, 0) : 0
      )
    );

    const maxDomain = Number.isFinite(maxDomainCandidate) && Number(maxDomainCandidate) > 0
        ? Number(maxDomainCandidate) * 1.1
        : 100;

    rootSvg
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom);

    const svg = rootSvg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const x = d3
      .scaleBand<string>()
      .domain(years)
      .range([0, width])
      .padding(0.28);

    const y = d3.scaleLinear().domain([0, maxDomain]).range([height, 0]).nice();

    svg
      .append("g")
      .attr("class", "grid")
      .call(d3.axisLeft(y).tickSize(-width).tickFormat(() => ""))
      .call((g) => g.select(".domain").remove())
      .call((g) =>
        g
          .selectAll(".tick line")
          .attr("stroke", "#eaecf0")
          .attr("stroke-dasharray", "4,3")
      );

    svg
      .append("g")
      .attr("transform", `translate(0, ${height})`)
      .call(d3.axisBottom(x).tickValues(x.domain().filter((_, i) => i % 2 === 0)))
      .call((g) => g.select(".domain").attr("stroke", "#d1d5db"))
      .call((g) => g.selectAll(".tick line").attr("stroke", "#d1d5db"))
      .call((g) =>
        g
          .selectAll(".tick text")
          .attr("fill", "#6b7280")
          .style("font-size", "11px")
          .style("font-family", "Inter, sans-serif")
      );

    svg
      .append("g")
      .call(d3.axisLeft(y).tickFormat(d3.format("~s")).ticks(6))
      .call((g) => g.select(".domain").remove())
      .call((g) => g.selectAll(".tick line").remove())
      .call((g) =>
        g
          .selectAll(".tick text")
          .attr("fill", "#6b7280")
          .style("font-size", "11px")
          .style("font-family", "Inter, sans-serif")
          .attr("dx", "-4")
      );

    svg
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", 0 - margin.left)
      .attr("x", 0 - height / 2)
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .style("font-size", "12px")
      .style("font-family", "Inter, sans-serif")
      .style("font-weight", "500")
      .style("fill", "#6b7280")
      .text(`Annual Emissions (${UNIT_TCO2E})`);

    const tooltip = d3.select(tooltipRef.current);

    const showTooltip = (event: MouseEvent, d: SafeInteractiveGlidepathMetric) => {
      const barColor = getBarColor(d);
      const hasScopeData =
        !d.isHistoricalEmissionData &&
        d.reductionsByScope &&
        scopeKeys.some((s) => (d.reductionsByScope![s] ?? 0) > 0);

      const targetSection =
        hasTargetData && d.target !== undefined
          ? `
        <div style="display: flex; align-items: center; gap: 8px; margin-top: 6px;">
          <span style="width: 10px; height: 2px; background: #f59e0b; display: inline-block; flex-shrink: 0; border-top: 2px dashed #f59e0b;"></span>
          <span style="color: #6b7280; font-size: 12px;">Target Emissions</span>
          <span style="margin-left: auto; font-weight: 700; color: #111827; font-size: 13px;">${formatEmissionValue(
            d.target
          )}</span>
        </div>`
          : "";

      const tooltipContent = `
        <div style="border-left: 4px solid ${barColor}; padding-left: 10px; margin-bottom: 6px;">
          <div style="font-size: 14px; font-weight: 700; color: #111827; margin-bottom: 3px;">${escapeHtml(String(d.year))}</div>
          <div style="color: #9ca3af; font-size: 10px; text-transform: uppercase; letter-spacing: 0.8px; font-weight: 600;">
            ${getEmissionDataLabel(d)}
          </div>
        </div>
        <div style="height: 1px; background: #f3f4f6; margin: 8px 0;"></div>
        <div style="display: flex; align-items: center; gap: 8px; margin-top: 8px;">
          <span style="width: 10px; height: 10px; border-radius: 2px; background: ${barColor}; display: inline-block; flex-shrink: 0;"></span>
          <span style="color: #6b7280; font-size: 12px;">${
            d.isBaseLine
              ? "Baseline Emissions"
              : d.isHistoricalEmissionData
              ? "Historical Emissions (Actual)"
              : "Projected Residual Emissions"
          }</span>
          <span style="margin-left: auto; font-weight: 700; color: #111827; font-size: 13px;">${formatEmissionValue(
            toSafeNumber(d.residualEmissions, 0)
          )}</span>
        </div>
        ${hasScopeData
          ? scopeKeys
              .filter((s) => toSafeNumber(d.reductionsByScope![s], 0) > 0)
              .map(
                (s) => `
          <div style="display: flex; align-items: center; gap: 8px; margin-top: 3px; padding-left: 18px;">
            <span style="width: 8px; height: 8px; border-radius: 2px; background: ${getScopeColor(scopeKeys, s)}; display: inline-block; flex-shrink: 0;"></span>
            <span style="color: #6b7280; font-size: 11px;">${escapeHtml(s)}</span>
            <span style="margin-left: auto; font-weight: 600; color: #374151; font-size: 11px;">${formatEmissionValue(toSafeNumber(d.reductionsByScope![s], 0))}</span>
          </div>`
              )
              .join("")
          : ""}
        <div style="display: flex; align-items: center; gap: 8px; margin-top: 6px;">
          <span style="width: 10px; height: 2px; background: #1a1a2e; display: inline-block; flex-shrink: 0;${d.isHistoricalEmissionData ? "" : " border-top: 2px dashed #1a1a2e;"}"></span>
          <span style="color: #6b7280; font-size: 12px;">${
            d.isHistoricalEmissionData
              ? "Historical Emissions Trajectory"
              : "BAU GHG Emissions"
          }</span>
          <span style="margin-left: auto; font-weight: 700; color: #111827; font-size: 13px;">${formatEmissionValue(
            d.ghgEmissions !== undefined ? toSafeNumber(d.ghgEmissions, 0) : 0
          )}</span>
        </div>
        ${targetSection}
      `;

      tooltip.style("opacity", 1).html(tooltipContent);
      positionTooltip(tooltipRef.current, event);
    };

    const hideTooltip = () => {
      tooltip.style("opacity", 0);
    };

    const singleBarData = safeData.filter(
      (d) =>
        d.isHistoricalEmissionData ||
        !d.reductionsByScope ||
        scopeKeys.every((s) => (d.reductionsByScope![s] ?? 0) <= 0)
    );

    svg
      .selectAll(".bar")
      .data(singleBarData)
      .enter()
      .append("rect")
      .attr("class", "bar")
      .attr("x", (d) => x(String(d.year)) ?? 0)
      .attr("y", height)
      .attr("width", x.bandwidth())
      .attr("height", 0)
      .attr("rx", 3)
      .attr("ry", 3)
      .attr("fill", (d) => getBarColor(d))
      .on("mouseover", (event, d) => showTooltip(event, d))
      .on("mousemove", (event, d) => showTooltip(event, d))
      .on("mouseout", hideTooltip)
      .transition()
      .duration(700)
      .ease(d3.easeCubicOut)
      .attr("y", (d) => y(toSafeNumber(d.residualEmissions, 0)))
      .attr("height", (d) => height - y(toSafeNumber(d.residualEmissions, 0)));

    const stackedBarData = safeData.filter(
      (d) =>
        !d.isHistoricalEmissionData &&
        d.reductionsByScope &&
        scopeKeys.some((s) => (d.reductionsByScope![s] ?? 0) > 0)
    );

    stackedBarData.forEach((d) => {
      let cumulative = 0;
      const activeScopes = scopeKeys.filter(
        (s) => toSafeNumber(d.reductionsByScope![s], 0) > 0
      );
      activeScopes.forEach((scope, segIdx) => {
        const value = toSafeNumber(d.reductionsByScope![scope], 0);
        const isTop = segIdx === activeScopes.length - 1;
        svg
          .append("rect")
          .attr("class", "bar scope-bar")
          .attr("x", x(String(d.year)) ?? 0)
          .attr("y", height)
          .attr("width", x.bandwidth())
          .attr("height", 0)
          .attr("rx", isTop ? 3 : 0)
          .attr("ry", isTop ? 3 : 0)
          .attr("fill", getScopeColor(scopeKeys, scope))
          .attr("fill-opacity", 0.40)
          .on("mouseover", (event) => showTooltip(event, d))
          .on("mousemove", (event) => showTooltip(event, d))
          .on("mouseout", hideTooltip)
          .transition()
          .duration(700)
          .ease(d3.easeCubicOut)
          .attr("y", y(cumulative + value))
          .attr("height", y(cumulative) - y(cumulative + value));
        cumulative += value;
      });
    });

    const lineGHG = d3
      .line<SafeInteractiveGlidepathMetric>()
      .defined(
        (d) =>
          d.ghgEmissions !== undefined &&
          Number.isFinite(Number(d.ghgEmissions))
      )
      .x((d) => (x(String(d.year)) ?? 0) + x.bandwidth() / 2)
      .y((d) => y(toSafeNumber(d.ghgEmissions, 0)))
      .curve(d3.curveMonotoneX);

    const lineSegments: {
      data: SafeInteractiveGlidepathMetric[];
      isHistorical: boolean;
    }[] = [];

    if (safeData.length > 0) {
      let currentSegment: SafeInteractiveGlidepathMetric[] = [safeData[0]];
      let currentIsHistorical = safeData[0].isHistoricalEmissionData;

      for (let i = 1; i < safeData.length; i++) {
        if (safeData[i].isHistoricalEmissionData === currentIsHistorical) {
          currentSegment.push(safeData[i]);
        } else {
          lineSegments.push({
            data: [...currentSegment],
            isHistorical: currentIsHistorical,
          });
          currentSegment = [safeData[i - 1], safeData[i]];
          currentIsHistorical = safeData[i].isHistoricalEmissionData;
        }
      }

      lineSegments.push({
        data: currentSegment,
        isHistorical: currentIsHistorical,
      });
    }

    lineSegments.forEach((segment) => {
      if (!segment.data.length) {
        return;
      }

      svg
        .append("path")
        .datum(segment.data)
        .attr("fill", "none")
        .attr("stroke", "#1a1a2e")
        .attr("stroke-width", segment.isHistorical ? 3 : 2.5)
        .attr("stroke-dasharray", segment.isHistorical ? "none" : "8,5")
        .attr("stroke-linecap", "round")
        .attr("d", lineGHG);
    });

    if (hasTargetData) {
      const targetData = safeData.filter(
        (d) => d.target !== undefined && Number.isFinite(Number(d.target))
      );

      if (targetData.length > 0) {
        const lineTarget = d3
          .line<SafeInteractiveGlidepathMetric>()
          .defined(
            (d) =>
              d.target !== undefined &&
              Number.isFinite(Number(d.target))
          )
          .x((d) => (x(String(d.year)) ?? 0) + x.bandwidth() / 2)
          .y((d) => y(toSafeNumber(d.target, 0)))
          .curve(d3.curveMonotoneX);

        svg
          .append("path")
          .datum(targetData)
          .attr("fill", "none")
          .attr("stroke", "#f59e0b")
          .attr("stroke-width", 2.5)
          .attr("stroke-dasharray", "7,5")
          .attr("stroke-linecap", "round")
          .attr("d", lineTarget);
      }
    }

    svg
      .selectAll(".ghg-dot")
      .data(
        safeData.filter(
          (d) =>
            d.ghgEmissions !== undefined &&
            Number.isFinite(Number(d.ghgEmissions))
        )
      )
      .enter()
      .append("circle")
      .attr("class", "ghg-dot")
      .attr("cx", (d) => (x(String(d.year)) ?? 0) + x.bandwidth() / 2)
      .attr("cy", (d) => y(toSafeNumber(d.ghgEmissions, 0)))
      .attr("r", 5)
      .attr("fill", "#fff")
      .attr("stroke", "#1a1a2e")
      .attr("stroke-width", 2.5)
      .on("mouseover", (event, d) => showTooltip(event, d))
      .on("mousemove", (event, d) => showTooltip(event, d))
      .on("mouseout", hideTooltip);

    if (hasTargetData) {
      svg
        .selectAll(".target-dot")
        .data(
          safeData.filter(
            (d) => d.target !== undefined && Number.isFinite(Number(d.target))
          )
        )
        .enter()
        .append("circle")
        .attr("class", "target-dot")
        .attr("cx", (d) => (x(String(d.year)) ?? 0) + x.bandwidth() / 2)
        .attr("cy", (d) => y(toSafeNumber(d.target, 0)))
        .attr("r", 5)
        .attr("fill", "#fff")
        .attr("stroke", "#f59e0b")
        .attr("stroke-width", 2.5)
        .on("mouseover", (event, d) => showTooltip(event, d))
        .on("mousemove", (event, d) => showTooltip(event, d))
        .on("mouseout", hideTooltip);
    }

    // Collect unique target marker names per year then draw shared vertical lines
    const markersByYear = new Map<number, string[]>();
    safeData.forEach((d) => {
      if (Array.isArray((d as any).targetMarkers)) {
        ((d as any).targetMarkers as { targetName: string; targetYear: number }[]).forEach((m) => {
          if (!markersByYear.has(m.targetYear)) markersByYear.set(m.targetYear, []);
          if (!markersByYear.get(m.targetYear)!.includes(m.targetName)) {
            markersByYear.get(m.targetYear)!.push(m.targetName);
          }
        });
      }
    });
    renderTargetMarkerLines(svg, markersByYear, x, height);
  }, [safeData, hasTargetData, scopeKeys]);

  const legendItems = [
    {
      label: "Historical Emissions (Actual)",
      color: "#5B9BD5",
      type: "rect" as const,
    },
    {
      label: "Baseline Year",
      color: "#ED7D31",
      type: "rect" as const,
    },
    {
      label: "Projected Residual Emissions",
      color: "#c5d4e8",
      type: "rect" as const,
    },
    {
      label: "Historical Emissions Trajectory",
      color: "#1a1a2e",
      type: "solid-line" as const,
    },
    {
      label: "BAU GHG Emissions (Projected)",
      color: "#1a1a2e",
      type: "dashed-line" as const,
    },
    ...(hasTargetData
      ? [
          {
            label: "Targeted GHG Emissions",
            color: "#f59e0b",
            type: "dashed-line" as const,
          },
        ]
      : []),
    ...scopeKeys.map((scope) => ({
      label: scope,
      color: getScopeColor(scopeKeys, scope),
      type: "rect" as const,
    })),
  ];

  if (!safeData.length) {
    return (
      <Grid item xs={12} sx={{ mb: 1, width: "100%", maxWidth: "100%" }}>
        <Paper
          elevation={3}
          sx={{
            mb: 1,
            width: "100%",
            overflow: "hidden",
            borderRadius: "14px",
            border: "0px solid #eef0f5",
            minHeight: 320,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ color: "#64748b", fontSize: "0.95rem" }}>
            No valid data available for Target Trajectory.
          </Typography>
        </Paper>
      </Grid>
    );
  }

  return (
    <Grid item xs={12} sx={{ mb: 1, width: "100%", maxWidth: "100%" }}>
      <Paper
        elevation={3}
        sx={{
          mb: 1,
          width: "100%",
          overflow: "hidden",
          borderRadius: "14px",
          border: "0px solid #eef0f5",
          position: "relative",
        }}
      >
        <div style={{ padding: "0 6px" }}>
          <svg ref={svgRef} width="100%" height={480} />
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "8px 10px",
            padding: "14px 24px 20px",
            borderTop: "1px solid #f0f2f6",
            backgroundColor: "#f8fafc",
          }}
        >
          {legendItems.map((item, i) => (
            <div
              key={`${item.label}-${i}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "11.5px",
                color: "#374151",
                fontFamily: "Inter, sans-serif",
                fontWeight: 500,
                backgroundColor: "#ffffff",
                border: "1px solid #e5e7eb",
                borderRadius: "20px",
                padding: "4px 12px 4px 8px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              }}
            >
              {item.type === "rect" && (
                <span
                  style={{
                    width: 11,
                    height: 11,
                    borderRadius: 2,
                    backgroundColor: item.color,
                    display: "inline-block",
                    flexShrink: 0,
                  }}
                />
              )}
              {item.type === "solid-line" && (
                <svg width="20" height="12" style={{ flexShrink: 0 }}>
                  <line
                    x1="0"
                    y1="6"
                    x2="20"
                    y2="6"
                    stroke={item.color}
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                </svg>
              )}
              {item.type === "dashed-line" && (
                <svg width="20" height="12" style={{ flexShrink: 0 }}>
                  <line
                    x1="0"
                    y1="6"
                    x2="20"
                    y2="6"
                    stroke={item.color}
                    strokeWidth="2.5"
                    strokeDasharray="4,3"
                    strokeLinecap="round"
                  />
                </svg>
              )}
              <span>{item.label}</span>
            </div>
          ))}
        </div>

        {createPortal(
          <div
            ref={tooltipRef}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              background: "#ffffff",
              padding: "14px 16px",
              border: "1px solid #e5e7eb",
              borderRadius: "10px",
              pointerEvents: "none",
              opacity: 0,
              fontSize: "13px",
              fontFamily: "Inter, sans-serif",
              boxShadow:
                "0 10px 25px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
              minWidth: "220px",
              zIndex: 1500,
              transition: "opacity 0.15s ease",
            }}
          />,
          document.body
        )}
      </Paper>
    </Grid>
  );
});

export default InteractiveGlidepath;